/**
 * MemberRosterScreen — list members of a district.
 *
 * Shows each member's user_id (truncated), role, and join date.
 * Defer per-user detail (last seen, caseload size, etc.) to a
 * later ticket; v1 just shows the roster + role badges.
 *
 * Issue #433: renders as a proper DataTable with sticky headers at
 * desktop. Mobile collapses to a card list (handled in CSS).
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../../lib/supabase/client';
import { usePublicPath } from '../../lib/routing/usePublicPath';
import {
  EmptyState,
  InviteIllustration,
} from '../../components/EmptyState';
import { CaseloadSkeleton } from '../../components/Skeleton';
import { humanError } from '../../lib/ui/errorCopy';
import { DataTable, type DataTableColumn } from '../../components/DataTable';

interface MemberRow {
  user_id: string;
  role: string;
  joined_at: string;
  email: string | null;
}

interface Props {
  orgId: string;
}

export function MemberRosterScreen({ orgId }: Props): ReactElement {
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { navigate } = usePublicPath();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error: e } = await supabase
        .from('organization_memberships')
        .select('user_id, role, joined_at, email')
        .eq('organization_id', orgId)
        .order('joined_at', { ascending: false });
      if (cancelled) return;
      if (e) {
        setError(humanError(e));
      } else {
        setMembers((data ?? []) as MemberRow[]);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  if (loading) {
    return (
      <div className="admin-members">
        <h2>Members</h2>
        <CaseloadSkeleton rows={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-members">
        <h2>Members</h2>
        <section className="empty-state" aria-live="assertive">
          <h2 className="empty-state-title">Couldn&rsquo;t load members</h2>
          <p className="empty-state-body">{error}</p>
          <button
            type="button"
            className="snap-button empty-state-cta"
            onClick={() => {
              window.location.reload();
            }}
          >
            Try again
          </button>
        </section>
      </div>
    );
  }

  const columns: DataTableColumn<MemberRow>[] = [
    {
      header: 'User ID',
      isRowHeader: true,
      cell: (m) => <code>{m.user_id.slice(0, 8)}</code>,
    },
    {
      header: 'Role',
      cell: (m) => <span className="chip">{m.role}</span>,
    },
    {
      header: 'Email',
      cell: (m) => m.email ?? '—',
    },
    {
      header: 'Joined',
      cell: (m) => new Date(m.joined_at).toLocaleDateString(),
    },
  ];

  return (
    <div className="admin-members">
      <h2>Members</h2>
      <DataTable<MemberRow>
        caption="Members"
        ariaLabel="District members"
        rowKey={(m) => m.user_id}
        columns={columns}
        rows={members}
        emptyState={
          <EmptyState
            illustration={<InviteIllustration />}
            title="No members yet"
            body="Invite your first SLP to get started."
            cta={{
              label: 'Invite an SLP',
              onClick: () => navigate('/admin/district/invites'),
            }}
          />
        }
      />
    </div>
  );
}
